import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Theats } from '../../../shared/types/training.type'
import { mergeArr, startTimeFrom0 } from '../../../shared/utils/MapSection'
import { Scale } from '../../../pages/trainings/ExamView'
import { get, isEmpty, isUndefined, toNumber } from 'lodash'
import { Box, Stack } from '@mui/material'
import HighchartsReact from 'highcharts-react-official'
import Highcharts, { PlotLineOrBand } from 'highcharts'
import { t } from 'i18next'
import { convertSpeed, getSpeedForChart, getSpeedTooltipForChart } from '../../../shared/utils/conversion'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import dayjs from 'dayjs'
import I18nDataGrid from '../../global/I18nDataGrid'
import HeatGrid from './HeatGrid'
import { useChart } from '../../../hooks/useChart'
import { useAuth } from '../../../hooks/useAuth'
import Duration from 'dayjs/plugin/duration'
import { CHART_COLOR_BPM, CHART_COLOR_SPEED } from '../../../enums/chatColors.enums'
import { TPlatformConfiguration } from '../../../shared/types/platformConfiguration'

dayjs.extend(Duration)

type IntervalTrainingProps = {
  speed: number[]
  bpm: number[],
  distance: number[],
  heats?: Theats[]
  time: number[],
  flIndex?: number | null,
  scaleOption: Scale,
  isFinishLineActive: boolean,
  printable?: boolean,
  labelsColor?: string,
  adaptiveDisplay?: boolean
}

function IntervalTraining({ labelsColor = '#fff', adaptiveDisplay = false, printable = false, speed, bpm, heats, time, scaleOption, distance, flIndex, isFinishLineActive }: IntervalTrainingProps) {
  const chartRef = useRef<HighchartsReact.RefObject>(null)
  const metric = useSelector<RootState>(state => state.user.user?.unit_system) as "metric" | "imperial" | undefined
  const [hoveredHeat, setHoveredHeat] = useState<number | null>(null)
  const { addFinishLine } = useChart()
  const [svg, setSvg] = useState<any[] | undefined>([])
  const { getSpeedUnit } = useAuth()
  const platformConfigurations = useSelector<RootState>(state => state.user.platformConfigurations) as TPlatformConfiguration[]

  const formattedTime = useMemo(() => {
    return startTimeFrom0(time)
  }, [time])

  //get heat range to slice the time and distance array
  const heatIndex = useMemo((): { start_index?: number, end_index?: number } => {
    if (isEmpty(heats)) return { start_index: undefined, end_index: undefined }
    let firstHeat = heats && heats[0].index_moment_start
    let lastHeat = heats && heats[heats.length - 1].index_moment_end

    return { start_index: firstHeat, end_index: lastHeat }
  }, [heats])

  //create plotBands from heats
  const plotBands = useMemo(() => {
    if (heats && heats.length > 0) {
      return heats.map((heat, index) => {

        return {
          id: heat.id,
          color: heat.is_work ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
          label: {
            text: index + 1,
          },
          from: scaleOption === 'DISTANCE' ? distance[heat.index_moment_start] : formattedTime[heat.index_moment_start],
          to: scaleOption === 'DISTANCE' ? distance[heat.index_moment_end] : formattedTime[heat.index_moment_end],
          events: {
            mouseover: function () {
              try {
                /* @ts-ignore */
                // this.style.fill = 'lightgray'
                this.svgElem.attr('fill', 'rgba(255, 255, 255, 0.5)')
                /* @ts-ignore */
                this['mydata:id'] = heat.id
                setHoveredHeat(heat.id)
              } catch (error) {
                console.log('Error on mouse over interval training:', error);

              }



            },
            mouseout: function () {
              try {
                /* @ts-ignore */
                this.svgElem.attr('fill', heat.is_work ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)')
                // this.style.fill = heat.is_work ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)'
                /* @ts-ignore */
                this['mydata:id'] = heat.id
                setHoveredHeat(null)
              } catch (error) {
                console.log('Error on mouse over interval training:', error);
              }


            },
          }
        }
      })
    } else {
      return []
    }
  }, [heats, scaleOption, distance, formattedTime])


  const timeSlice = useMemo((): number[] => {
    if (formattedTime.length > 0 && Object.values(heatIndex).every(e => e)) {
      return formattedTime.slice(heatIndex.start_index, heatIndex.end_index)
    } else {
      return []
    }
  }, [formattedTime, heatIndex])

  const getMaxYValueSpeed = useMemo(() => {
    const DEFAULT_MAX_VALUE = 249
    try {


      let findMaxValue = platformConfigurations.find(config => config.key === 'MAX_VALUE_SPEED_CHART_KMH')

      return findMaxValue?.value ? (Number(findMaxValue?.value) / 0.3) : DEFAULT_MAX_VALUE
    } catch (error) {
      console.error(error);
      return DEFAULT_MAX_VALUE
    }
  }, [platformConfigurations])

  const distanceSlice = useMemo((): number[] => {
    if (distance.length > 0 && Object.values(heatIndex).every(e => !isUndefined(e))) {
      return distance.slice(heatIndex.start_index, heatIndex.end_index)
    } else {
      return []
    }
  }, [distance, heatIndex])

  const speedSlice = useMemo((): number[] => {
    if (speed.length > 0 && Object.values(heatIndex).every(e => !isUndefined(e))) {
      return speed.slice(heatIndex.start_index, heatIndex.end_index)
    } else {
      return []
    }
  }, [speed, heatIndex])

  const bpmSlice = useMemo((): number[] => {
    if (bpm.length > 0 && Object.values(heatIndex).every(e => !isUndefined(e))) {
      return bpm.slice(heatIndex.start_index, heatIndex.end_index)
    } else {
      return []
    }
  }, [bpm, heatIndex])


  useEffect(() => {

    if (!isFinishLineActive) return

    try {
      if (svg && !isEmpty(svg)) {
        svg.forEach((svg: any) => svg.destroy())
        setSvg([])
      }
    } catch (err) {

    }

    let data = addFinishLine(flIndex ?? null, scaleOption, distance, formattedTime, chartRef.current?.chart)
    setSvg(data)
  }, [flIndex, scaleOption, formattedTime, distance, isFinishLineActive])


  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart || !chart.container) return;

    const container = chart.container;

    const handleMouseLeave = () => {
      chart.tooltip.hide();
      chart.pointer.reset?.(); // Reset hover states (defensive check in case pointer is undefined)
    };

    container.addEventListener('mouseleave', handleMouseLeave);

    // Cleanup
    return () => {
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [chartRef.current?.chart]); // <- Make sure effect re-runs when chart is mounted

  const options = useMemo(() => {
    return {
      chart: {
        type: 'line',
        zoomType: "x",
        backgroundColor: 'transparent',
        animation: false,
        spacingTop: 20,

      },
      credits: {
        enabled: false
      },
      title: {
        text: ''
      },
      legend: { enabled: false },
      plotOptions: {
        series: {
          states: {
            hover: {
              lineWidthPlus: 0 // Prevents line width from increasing on hover

            }
          },
        }
      },
      yAxis: [{
        gridLineColor: 'transparent',
        tickAmount: 4,
        // 1. Critical: Prevent Highcharts from syncing this axis scale with others
        alignTicks: false,

        // 2. Lock the max to your DB value (e.g., 183.33)
        max: getMaxYValueSpeed,

        // 3. Prevent rounding up to the next "nice" number (like 240)
        endOnTick: false,
        startOnTick: false,

        // 4. Force exact positions so it doesn't calculate 0, 60, 120, 180, 240
        tickPositions: [
          0,
          getMaxYValueSpeed * 0.33,
          getMaxYValueSpeed * 0.66,
          getMaxYValueSpeed
        ],
        labels: {
          style: {
            color: CHART_COLOR_SPEED
          },
          /* @ts-ignore */
          formatter: function () {
            /* @ts-ignore */
            return getSpeedForChart(toNumber(this.value), getSpeedUnit()?.value ?? "")
          }
        }, // Primary yAxis
        title: {
          text: t('graph:vitesse') + ` (${t(getSpeedUnit()?.key ?? "")})`,
          style: {
            color: CHART_COLOR_SPEED
          },
        },
        opposite: false
      }, { // Primary yAxis
        gridLineColor: 'transparent',
        title: {
          text: t('graph:bpm'),
          style: {
            color: CHART_COLOR_BPM
          }
        },
        labels: {
          style: {
            color: CHART_COLOR_BPM
          },
        },
        opposite: true
      }],
      xAxis: {
        plotBands,
        gridLineColor: 'transparent',
        crosshair: {
          width: 1,
        },
        type: scaleOption === 'DISTANCE' ? 'linear' : 'datetime',
        labels: {
          style: {
            color: labelsColor
          },
          /* @ts-ignore */
          formatter: function () {
            /* @ts-ignore */
            return scaleOption === 'DISTANCE' ? convertSpeed(metric, this.value) : dayjs.duration(this.value, 'seconds').minutes() + ' mn'
          }
        }
      },
      tooltip: {
        shared: true,
        shadow: false,
        backgroundColor: 'transparent',
        useHTML: true, // Required if rendering outside

        style: {
          fontWeight: 'bold',
          fontSize: '12px',
          color: labelsColor
        },
        positioner: function (
          this: Highcharts.Tooltip,
          labelWidth: number,
          labelHeight: number,
          point?: Highcharts.Point
        ): Highcharts.PositionObject {
          const chart = this.chart;
          return {
            x: chart.plotLeft + chart.plotWidth - labelWidth - 10,
            y: chart.plotTop - 25
          };
        },
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          console.log({ t: this });

          let str = ''
          str += this?.points && this?.points[1]?.y + " " + t('graph:bpm') + ' - '
          /* @ts-ignore */
          str += getSpeedTooltipForChart(getSpeedForChart(toNumber(this.points[0].y), getSpeedUnit()?.value), getSpeedUnit()?.value)
          return str

        },
      },
      series: [
        {
          yAxis: 0,
          data: mergeArr(scaleOption === 'DISTANCE' ? distanceSlice : timeSlice, speedSlice),
          color: CHART_COLOR_SPEED,
          name: 'SPEED'
        },
        {
          yAxis: 1,
          data: mergeArr(scaleOption === 'DISTANCE' ? distanceSlice : timeSlice, bpmSlice),
          color: CHART_COLOR_BPM,
          name: 'BPM'

        },
      ]
    }
  }, [formattedTime, scaleOption, distanceSlice, timeSlice, plotBands, speedSlice, bpmSlice])

  const onRowHover = (item?: Theats, action?: 'entered' | 'leave') => {
    let chart = chartRef.current?.chart
    if (!item || !chart) return

    let plots = get(chart, 'xAxis[0].plotLinesAndBands', []) as Highcharts.PlotLineOrBand[]
    if (plots.length > 0) {
      let plot: Highcharts.PlotLineOrBand | undefined = plots.find(e => get(e, 'id') === item?.id)
      if (plot) {

        plot.svgElem.attr({ fill: action === 'entered' ? 'lightgray' : item.is_work ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)' })
      }
    }

  }

  return (
    <Stack id='intervalTraining-section' direction={'column'} spacing={3} mt={3}>
      <Box
        sx={{
          backgroundColor: adaptiveDisplay ? 'white' : 'transparent',
          padding: 2,
          borderRadius: 1,
        }}
      >

        <HighchartsReact
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { height: '220px' } }}
        />
      </Box>
      <HeatGrid printable={printable} hoveredHeat={hoveredHeat} onRowHover={onRowHover} speedUnit={t(getSpeedUnit()?.key ?? "")} heats={heats}></HeatGrid>
    </Stack>
  )
}

export default IntervalTraining